import { FormEvent, useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogClose,
	DialogFooter,
} from "@/components/ui/dialog";
import {
  Pagination,
  PaginationContent,
  PaginationItem,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination";
import { CodeDetailType, GetCodeDetailListParams, Partner, TIME_OPTIONS, VIP_STATUS_OPTIONS } from "@/types/code";
import { codeService } from "@/services/code";
import { toast } from "sonner";
import { DateUtils } from "@/utils/dateUtils";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Download, Search } from "lucide-react";
import { Textarea } from "@/components/ui/textarea";
import { useNavigate, useSearchParams } from "react-router-dom";

export default function CodeDetail() {
	// 分页
	const [page, setPage] = useState(1);
	const [pageSize] = useState(10);
	const [total, setTotal] = useState(0);

	// 路由信息
	const navigate = useNavigate()
	// query信息
	const [searchParams] = useSearchParams()
	const id = searchParams.get("id")

	// 数据
	const [loading, setLoading] = useState(true);

	// 保存进入详情页的原始平台、合作伙伴信息
	const [originalPlatform,setOriginalPlatform] = useState("")
	const [originalPartner,setOriginalPartner] = useState("")

	// 保存原始数据
	const [codes, setCodes] = useState<CodeDetailType[]>([]);
	const [platformList,setPlatformList] = useState<{id: string,label: string}[]>([])
	const [cardTypeList,setCardTypeList] = useState<{id: string,label: string}[]>([])
	const [trialCardList,setTrialCardList] = useState<{id: string,label: string,system_dict_id: string}[]>([])
	const [officialCardList,setOfficialCardList] = useState<{id: string,label: string,system_dict_id: string}[]>([])
	const [giftList,setGiftList] = useState<{id: string,label: string,system_dict_id: string}[]>([])

	// 1) 合作伙伴筛选
	const [platformSearch,setPlatformSearch] = useState<string>("")
	const [partnerSearch,setPartnerSearch] = useState<string>("")
	const [partnerList,setPartnerList] = useState<Partner[]>()

	// 2) 兑换码名称筛选
	const [field, setField] = useState<"codeName" | "phone" | "">("");
	const [codeName,setCodeName] = useState<string>("")
	// const [keyword, setKeyword] = useState("");

	// 3) 条件查询-时间筛选
	const [time,setTime] = useState<"created_at" | "valid_time" | "invalid_time">()
	const [timeStart, setTimeStart] = useState<string>();
	const [timeEnd, setTimeEnd] = useState<string>();

	// 4) 卡类型筛选
	const [cardTypeSearch,setCardTypeSearch] = useState("0")
	const [cardDurationSearch,setCardDurationSearch] = useState("0")

	// 5) 兑换状态筛选
	const [verifiedStatus,setVerifiedStatus] = useState<"pending"|"exchanged"|"0">("0")

	// 5) VIP状态筛选
	const [vipStatus,setVIPStatus] = useState<"active"|"inactive"|"0">("0")



	// 设置生效、失效
  const [selectedCode,setSelectedCode] = useState<CodeDetailType | null>()

	// 设置生效、设置失效弹窗相关状态
  const [invalidateDialogOpen,setInvalidateDialogOpen] = useState(false)
	const formRef = useRef<HTMLFormElement>(null);
	const [invalidReason,setInvalidReason] = useState("")

	// 批量设置失效、批量核销 得到响应后，隐藏下方操作按钮
	const [betchResponse,setBetchResponse] = useState<boolean>(false)
	// 批量设置失效相关状态
	const [invalidBatchDialogOpen,setInvalidBatchDialogOpen] = useState(false)
	const [excelData,setExcelData] = useState<string[] | {[key: string]: string}[]>([]) // 批量设置生效和批量核销共用
	const invalidBatchFormRef = useRef<HTMLFormElement>(null)

	// 批量核销相关状态
	const [checkBatchDialogOpen,setCheckBatchDialogOpen] = useState(false)
	const checkBatchFormRef = useRef<HTMLFormElement>(null)

	// 批量核销、批量设置失效接口返回的操作成功的兑换码数据
	const [batchSettingResList,setBatchSettingResList] = useState<string[]>([])

	// 导出弹窗相关状态
	const [exportDialogOpen, setExportDialogOpen] = useState(false);
	const [processingExport, setProcessingExport] = useState(false);

	const totalPages = useMemo(
		() => Math.max(1, Math.ceil(total / pageSize)),
		[total, pageSize]
	);
	const placeholderByField: Record<"codeName" | "phone", string> = {
    codeName: "请输入兑换码",
    phone: "请输入邀请人手机号",
  };

	// 进入详情页面后，获取默认的平台、合作伙伴信息
	const getDefaultDetails = async () => {
		if(!id)return;
		try{
			const res = await codeService.getCodesDetail(id)
			setOriginalPlatform(res.PlatformID)
			setOriginalPartner(res.PartnerID)
			setPlatformSearch(String(res.PlatformID))
			setPartnerSearch(String(res.PartnerID))
		}catch(e){
			console.error(e);
			toast.error("获取兑换码批次详情失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		}
	}
	useEffect(() => {
		getDefaultDetails()
	},[])

	// UI状态 -> 接口参数映射
	const buildRequestParams = () => {
		const params: GetCodeDetailListParams = {
			page,
			page_size: pageSize,
		};
		// 合作伙伴是否有所变动
		const ifPlatformChange = originalPlatform != platformSearch
		const ifPartnerChange = originalPartner != partnerSearch
		// 是否增加了其他筛选项
		const ifFilterItemChange = !!codeName || !!time || !!timeStart || !!timeEnd || cardTypeSearch !=="0" || cardDurationSearch !=="0" || verifiedStatus !=="0" || vipStatus !=="0"

		// 筛选项有变更
		if(ifPlatformChange || ifPartnerChange || ifFilterItemChange){
			if(codeName){
				if(field === "codeName"){
					params.code = codeName
				}
				if(field === "phone"){
					params.phone = codeName
				}
			}
			if(platformSearch){
				params.platform_id = Number(platformSearch)
			}
			if(partnerSearch){
				params.partner_id = Number(partnerSearch)
			}
			if(time){
				params.time_field = time
			}
			if(timeStart){
				params.start_time = DateUtils.toChineseISOString(timeStart, false)
			}
			if(timeEnd){
				params.end_time = DateUtils.toChineseISOString(timeEnd, true)
			}
			// 接口未增加参数
			if(cardTypeSearch && cardTypeSearch !=="0"){
				params.card_type = Number(cardTypeSearch)
			}
			if(cardDurationSearch !=="0"){
				params.card_spec = cardDurationSearch
			}
			if(verifiedStatus !=="0"){
				params.exchange_status = verifiedStatus
			}
			if(vipStatus !=="0"){
				params.vip_status = vipStatus
			}
		}else{
			params.code_id = Number(id)
		}
		return params;
	};
	const fetchCodes = async () => {
		setLoading(true);
		try {
			const params = buildRequestParams();
			const res = await codeService.getCodesDetailList({
				...params
			}) as {list?: CodeDetailType[],total?: number};

			// 更新映射逻辑以匹配API返回的结构
			const list = res.list ?? [];
			const totalCount = res.total ?? 0;
			setCodes(list);
			setTotal(totalCount);
		} catch (e) {
			console.error(e);
			toast.error("获取兑换码列表失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		} finally {
			setLoading(false);
		}
	};
	const openSetInvalidDialog = (code: CodeDetailType) => {
		setSelectedCode(code)
		setInvalidateDialogOpen(true)
	}
	const fetchPartners = async () => {
		// setLoading(true);
		try {
			const res = await codeService.getPartners({
				page: 1,
				page_size:99,
				platform_id: Number(platformSearch),
			}) as {list?: Partner[]};
			// 更新映射逻辑以匹配API返回的结构
			const list = res.list ?? [];
			setPartnerList(list)
		} catch (e) {
			console.error(e);
			toast.error("获取合作伙伴列表失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		} finally {
			// setLoading(false);
		}
	}

	useEffect(() => {
    fetchPartners()
  },[platformSearch])
	// 打开导出确认弹窗
	const handleExport = () => {
		setExportDialogOpen(true);
	};
	// 处理Excel数据
	const dealWithExcelData = (e: React.ChangeEvent<HTMLInputElement>) => {
		const files = e.target.files
		if(!files?.length) return;
		const file = files[0]
		const reader = new FileReader()
		reader.onload = (e) => {
			const data = new Uint8Array(e.target?.result as Iterable<number>)
			const workbook = XLSX.read(data ,{type: "array"})
			const firstSheetName = workbook.SheetNames[0]
			const worksheet = workbook.Sheets[firstSheetName]
			const jsonData = XLSX.utils.sheet_to_json(worksheet,{header: 1})
			if(!jsonData[0]){
				toast.error("excel数据为空,请重新上传")
				return;
			}
			const codeIndex = jsonData[0].findIndex( (colName: string) => colName == "兑换码")
			if(codeIndex === -1){
				toast.error("请上传包含兑换码的excel文件")
				return;
			}
			// 批量核销和批量设置失效分开处理
			if(checkBatchDialogOpen){
				const phoneIndex = jsonData[0].findIndex( (colName: string) => colName == "绑定手机号")
				if(phoneIndex === -1){
					toast.error("请上传包含绑定手机号的excel文件")
					return;
				}
				const sheetData = jsonData.slice(1) as string[][]
				const datas: {[key: string]: string}[] = []
				sheetData.map((arr: string[]) => {
					const obj:{[key: string]: string} = {}

					// obj[arr[codeIndex]] = arr[phoneIndex]
					obj[arr[phoneIndex]] = arr[codeIndex]
					datas.push(obj)
				})
				setExcelData(datas)
			}else {
				const sheetData = jsonData.slice(1) as string[][]
				setExcelData(sheetData.map((arr: string[]) =>arr[codeIndex]))
			}
		}
		reader.readAsArrayBuffer(file)
	}
	// 设置失效弹窗打开状态
	const openSetInvalidDialogOpenChange = (open: boolean) => {
		if(!open){
			setInvalidateDialogOpen(false)
			setInvalidReason("")
		}
	}
	// 设置失效表单提交
	const onSetInvalidFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		if(!selectedCode?.id) return;
		try{
			await codeService.setInvalidBatch([selectedCode.Code])
			await fetchCodes()
			openSetInvalidDialogOpenChange(false)

    }catch(err){
      console.error(err);
      toast.error("操作失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }

	}

	// 批量设置失效弹窗打开状态
	const invalidBatchDialogOpenChange = (open: boolean) => {
		if(!open){
			setInvalidBatchDialogOpen(false)
			setExcelData([])
			setBatchSettingResList([])
			setBetchResponse(false)
		}
	}
	// 批量设置失效表单提交
	const onInvalidBatchFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		try{
			const res = await codeService.setInvalidBatch(excelData as string[])
			setBatchSettingResList(res?.list||[])
			setBetchResponse(true)
			await fetchCodes()

    }catch(err){
      console.error(err);
      toast.error("操作失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }
	}
	// 批量核销弹窗打开状态
	const checkBatchDialogOpenChange = (open: boolean) => {
		if(!open){
			setCheckBatchDialogOpen(false)
			setExcelData([])
			setBatchSettingResList([])
			setBetchResponse(false)
		}
	}
	// 批量核销表单提交
	const onCheckBatchFormSubmit = async (e: FormEvent<HTMLFormElement>) => {
		e.preventDefault()
		try{
			let checkParams = {};
			(excelData as { [key: string]: string }[]).map((item: { [key: string]: string }) => {
				checkParams = {...checkParams,...item}
			})
			const res = await codeService.setCheckBatch(checkParams)
			setBatchSettingResList(res?.list || [])
			setBetchResponse(true)
			await fetchCodes()

    }catch(err){
      console.error(err);
      toast.error("操作失败", {
        description: err instanceof Error ? err.message : "请检查表单内容",
      });
    }
	}

	// 执行实际导出操作
	const confirmExport = async () => {
		setProcessingExport(true);
		try {
			// 构建请求参数，与 getInvitations 使用相同的参数结构
			const params = buildRequestParams();

			// 调用 inviteService 的 exportInvitations 方法
			const blob = await codeService.downloadCodes(params);

			// 创建下载链接
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `兑换码批次记录_${new Date().toISOString().split("T")[0]}.xlsx`;
			document.body.appendChild(a);
			a.click();

			// 清理
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success("导出成功", {
				description: "兑换码批次记录已成功导出",
			});

			// 关闭弹窗
			setExportDialogOpen(false);
		} catch (e) {
			console.error(e);
			toast.error("导出失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		} finally {
			setProcessingExport(false);
		}
	};

	useEffect(() => {
		fetchCodes();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, pageSize]);
	const getOptions = async () => {
		try{
			const res = await codeService.getOptionsList("platform,card,trialCard,officialCard,gifts")
			setPlatformList(res.platform)
			setCardTypeList(res.card)
			setTrialCardList(res.trialCard)
			setOfficialCardList(res.officialCard)
			setGiftList(res.gifts.reverse())
		}catch(e){
			console.error(e);
			toast.error("获取筛选项列表失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		}
	}
	useEffect(() => {
		getOptions()
	},[])
	const resetFilters = () => {
		setField("")
		setTimeStart("");
		setTimeEnd("");
    setPlatformSearch("")
    setPartnerSearch("")
    setCodeName("")
    setCardTypeSearch("")
		setTime("" as "created_at" | "valid_time" | "invalid_time")
		setPage(1);
		setVerifiedStatus("0")
		setVIPStatus("0")
	};
	const handleSearch = () => {
		setPage(1);
		fetchCodes();
	};
	const goBack = () => {
		navigate(-1)
	}
	const downloadTemplate = async (request: () => Promise<Blob>,type: "invalidate" | "verify") => {
		try{
			const blob = await request();
			// 创建下载链接
			const url = window.URL.createObjectURL(blob);
			const a = document.createElement("a");
			a.href = url;
			a.download = `${type === "invalidate"?"批量设置失效":"批量核销"}模板_${new Date().toISOString().split("T")[0]}.xlsx`;
			document.body.appendChild(a);
			a.click();

			// 清理
			window.URL.revokeObjectURL(url);
			document.body.removeChild(a);

			toast.success("下载完成", {
				description: "文件模板已下载完成",
			});
		}catch(e){
			console.error(e);
			toast.error("模板下载失败", {
				description: e instanceof Error ? e.message : "请稍后重试",
			});
		}
	}

  return (
    <div className="space-y-4 h-full flex flex-col">
			<div className="flex items-center justify-between">
				<h2 className="text-2xl font-bold tracking-tight"><ArrowLeft className="inline-block cursor-pointer mr-4" onClick={goBack}/>兑换码详情</h2>
			</div>
			{/* 筛选区 - 现在分为两行 */}
    	<div className="rounded-lg border p-4 space-y-4">
    	  {/* 第一行 */}
    	  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
					{/* 1. 兑换码名称 */}
    	    <div className="flex flex-col gap-2">
    	      <label className="text-sm text-gray-600">条件查询</label>
						<div className="flex gap-2">
							<Select
								value={field}
								onValueChange={(v:"codeName" | "phone" | "") => setField(v)}
							>
								<SelectTrigger className="w-40">
									<SelectValue placeholder="选择字段" />
								</SelectTrigger>
								<SelectContent>
									<SelectItem value="codeName">兑换码</SelectItem>
									<SelectItem value="phone">手机号</SelectItem>
								</SelectContent>
							</Select>
							<Input
								placeholder={field && placeholderByField[field]}
								value={codeName}
								onChange={(e) => setCodeName(e.target.value)}
								onKeyDown={(e) => e.key === "Enter" && handleSearch()}
								className="flex-1"
							/>
						</div>
    	      {/* <div className="flex gap-3">
    	        <Input
    	          value={codeName}
    	          onChange={(e)=>setCodeName(e.target.value)}
    	          placeholder="请输入兑换码名称"
    	        />
    	      </div> */}
    	    </div>
    	    {/* 2. 合作伙伴 */}
    	    <div className="flex flex-col gap-2">
    	      <label className="text-sm text-gray-600">合作伙伴</label>
    	      <div className="flex gap-2">
    	        <Select
    	          value={platformSearch}
    	          onValueChange={(v) => {
    	            setPlatformSearch(v)
    	          }}
    	        >
    	          <SelectTrigger className="w-40">
    	            <SelectValue placeholder="选择平台" />
    	          </SelectTrigger>
    	          <SelectContent>
    	            {
    	              platformList?.map(({label,id}) => {
    	                return <SelectItem key={id} value={id}>{label}</SelectItem>
    	              })
    	            }
    	          </SelectContent>
    	        </Select>
    	        <Select
    	          value={partnerSearch}
    	          onValueChange={(v) => setPartnerSearch(v)}
    	        >
    	          <SelectTrigger className="w-40">
    	            <SelectValue placeholder="选择合作伙伴" />
    	          </SelectTrigger>
    	          <SelectContent>
    	            {
    	              partnerList?.length?partnerList.map(item => {
    	                return <SelectItem key={item.id} value={item.id}>{item.Name}</SelectItem>
    	              }):"暂无合作伙伴"
    	            }
    	          </SelectContent>
    	        </Select>
    	      </div>
    	    </div>
    	    {/* 3. 时间 */}
    	    <div className="flex flex-col gap-2 col-span-2">
    	      <label className="text-sm text-gray-600">时间</label>
    	      <div className="flex gap-3">
							<Select
    	          value={time}
    	          onValueChange={(v: "created_at" | "valid_time" | "invalid_time") => setTime(v)}
    	        >
    	          <SelectTrigger className="w-60">
    	            <SelectValue placeholder="选择字段" />
    	          </SelectTrigger>
    	          <SelectContent>
    	            {
    	              TIME_OPTIONS.map(({key,value}) => {
    	                return <SelectItem key={key} value={key}>{value}</SelectItem>
    	              })
    	            }
    	          </SelectContent>
    	        </Select>
    	        <Input
    	          type="date"
    	          value={timeStart}
    	          onChange={(e) => setTimeStart(e.target.value)}
    	        />
    	        <Input
    	          type="date"
    	          value={timeEnd}
    	          onChange={(e) => setTimeEnd(e.target.value)}
    	        />
    	      </div>
    	    </div>
    	  </div>
    	  {/* 第二行 */}
    	  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
    	    {/* 4. 卡类型 */}
    	    <div className="flex flex-col gap-2">
    	      <label className="text-sm text-gray-600">卡类型</label>
    	      <div className="flex gap-2">
    	        <Select
    	          value={cardTypeSearch}
    	          onValueChange={(v) => setCardTypeSearch(v)}
    	        >
    	          <SelectTrigger className="w-60">
    	            <SelectValue placeholder="选择卡类型" />
    	          </SelectTrigger>
    	          <SelectContent>
									<SelectItem value="0" key="0">不限</SelectItem>
    	            {
    	              cardTypeList.map(({label,id}) => {
    	                return <SelectItem key={id} value={id}>{label}</SelectItem>
    	              })
    	            }
    	          </SelectContent>
    	        </Select>
    	        <Select
    	          value={cardDurationSearch}
    	          onValueChange={(v) => setCardDurationSearch(v)}
    	        >
    	          <SelectTrigger className="w-60">
    	            <SelectValue placeholder="选择会员时长" />
    	          </SelectTrigger>
    	          <SelectContent>
									<SelectItem value="0" key="0">不限</SelectItem>
    	            {
    	              [...officialCardList,...trialCardList].filter(({system_dict_id})=>{
    	                if(cardTypeSearch == undefined || cardTypeSearch == "0"){
    	                  return true
    	                }
    	                return system_dict_id == cardTypeSearch
    	              }).map(({id,label}) => {
    	                return <SelectItem key={id} value={id}>{label}</SelectItem>
    	              })
    	            }
    	          </SelectContent>
    	        </Select>
    	      </div>
    	    </div>
			{/* 5. 兑换状态*/}
			<div className="flex flex-col gap-2">
    	      <label className="text-sm text-gray-600">兑换状态</label>
    	      <div className="flex gap-2">
    	        <Select
    	          value={verifiedStatus}
    	          onValueChange={(v:"pending"|"exchanged"|"0") => setVerifiedStatus(v)}
    	        >
    	          <SelectTrigger className="w-60">
    	            <SelectValue placeholder="选择订单状态" />
    	          </SelectTrigger>
    	          <SelectContent>
									<SelectItem value="0">不限</SelectItem>
									<SelectItem value="exchanged">已兑换</SelectItem>
									<SelectItem value="pending">未兑换</SelectItem>
    	            {/* {
    	              TIME_OPTIONS.map(({key,value}) => {
    	                return <SelectItem key={key} value={key}>{value}</SelectItem>
    	              })
    	            } */}
    	          </SelectContent>
    	        </Select>
    	      </div>
    	    </div>
					{/* 5. VIP状态*/}
					<div className="flex flex-col gap-2">
    	      <label className="text-sm text-gray-600">VIP状态</label>
    	      <div className="flex gap-2">
    	        <Select
    	          value={vipStatus}
    	          onValueChange={(v: "active"|"inactive"|"0") => setVIPStatus(v)}
    	        >
    	          <SelectTrigger className="w-60">
    	            <SelectValue placeholder="选择VIP状态" />
    	          </SelectTrigger>
    	          <SelectContent>
									<SelectItem value="0">不限</SelectItem>
									<SelectItem value="active">已激活</SelectItem>
									<SelectItem value="inactive">未激活</SelectItem>
    	          </SelectContent>
    	        </Select>
    	      </div>
    	    </div>
    	    {/* 7. 操作按钮组 */}
    	    <div className="flex flex-col gap-2 justify-end column-2 col-end-5">
    	      <label className="text-sm text-gray-600 opacity-0">操作</label>
    	      <div className="flex gap-2 justify-end">
    	        <Button className="w-24" variant="outline" onClick={resetFilters}>
    	          重置
    	        </Button>
    	        <Button className="w-24" onClick={handleSearch}>
    	          <Search className="mr-2 h-4 w-4" />
    	          查询
    	        </Button>
    	        <Button
    	          className="w-24"
    	          variant="secondary"
    	          onClick={handleExport}
    	        >
    	          <Download className="mr-2 h-4 w-4" />
    	          导出
    	        </Button>
    	        <Button className="w-24" onClick={() => setInvalidBatchDialogOpen(true)}>
    	          批量设置失效
    	        </Button>
							<Button className="w-24" onClick={()=> setCheckBatchDialogOpen(true)}>
    	          批量核销
    	        </Button>
    	      </div>
    	    </div>
    	  </div>
    	</div>
			{/* 列表：可滚动容器 */}
			<div className="rounded-md border overflow-auto max-h-[60vh]">
				<Table>
					<TableHeader>
						<TableRow>
							<TableHead>选项</TableHead>
							<TableHead className="whitespace-nowrap">批次</TableHead>
							<TableHead className="whitespace-nowrap">平台</TableHead>
							<TableHead className="whitespace-nowrap">合作伙伴</TableHead>
							<TableHead className="whitespace-nowrap">兑换码名称</TableHead>
							<TableHead className="whitespace-nowrap">类型</TableHead>
							<TableHead className="whitespace-nowrap">赠品</TableHead>
							<TableHead className="whitespace-nowrap">生效时间</TableHead>
							<TableHead className="whitespace-nowrap">兑换码</TableHead>
							<TableHead className="whitespace-nowrap">绑定手机号</TableHead>
							<TableHead className="whitespace-nowrap">绑定时间</TableHead>
							<TableHead className="whitespace-nowrap">兑换状态</TableHead>
							<TableHead className="whitespace-nowrap">VIP状态</TableHead>
							<TableHead className="whitespace-nowrap">核销状态</TableHead>
							<TableHead className="whitespace-nowrap">核销时间</TableHead>
							<TableHead className="whitespace-nowrap">激活时间</TableHead>
							<TableHead className="whitespace-nowrap">退款时间</TableHead>
							<TableHead className="whitespace-nowrap">备注</TableHead>
							<TableHead className="whitespace-nowrap">操作</TableHead>
						</TableRow>
					</TableHeader>
					<TableBody>
						{loading ? (
							<TableRow>
								<TableCell colSpan={19} className="h-24 text-center">
									<div className="flex justify-center items-center">
										<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900"></div>
									</div>
								</TableCell>
							</TableRow>
						) : codes.length === 0 ? (
							<TableRow>
								<TableCell colSpan={19} className="h-24 text-center">
									没有找到兑换码记录
								</TableCell>
							</TableRow>
						) : (
							codes.map((code) => (
								<TableRow key={code.id}>
									{/* 选项 */}
									<TableCell className="whitespace-nowrap">
										{/* {code.id || "-"} */}
									</TableCell>
									{/* 批次 */}
									<TableCell className="whitespace-nowrap">
										{code.BatchNo || "-"}
									</TableCell>
									{/* 平台 */}
									<TableCell className="whitespace-nowrap">
										{code?.ExchangeCode?.PlatformID
											?platformList.filter((item) => Number(item.id) == code?.ExchangeCode?.PlatformID)[0]?.label
											:"-"
										}
									</TableCell>
									{/* 合作伙伴 */}
									<TableCell className="whitespace-nowrap">
										{/* 具体对应值待更新 */}
										{code?.ExchangeCode?.PartnerName||"-"}
									</TableCell>
									{/* 兑换码名称 */}
									<TableCell className="whitespace-nowrap">
										{code.Code || "-"}
									</TableCell>
									{/* 类型 */}
									<TableCell className="whitespace-nowrap">
										{code?.ExchangeCode?.CardSpecID
											?[...officialCardList,...trialCardList].filter((item) => Number(item.id) == code?.ExchangeCode?.CardSpecID)[0]?.label
											: "-"
										}
									</TableCell>
									{/* 赠品 */}
									<TableCell className="whitespace-nowrap">
										{code.ExchangeCode.GiftID?giftList.filter((item) => item.id == String(code.ExchangeCode.GiftID))[0]?.label:"-"}
									</TableCell>
									{/* 生效时间 */}
									<TableCell className="whitespace-nowrap">
										{`${DateUtils.formatDate(new Date(code.ExchangeCode.StartTime))}-${DateUtils.formatDate(new Date(code.ExchangeCode.EndTime))}`}
									</TableCell>
									{/* 兑换码 */}
									<TableCell className="whitespace-nowrap">
										{code.ExchangeCodeID || "-"}
									</TableCell>
									{/* 手机号 */}
									<TableCell className="whitespace-nowrap">
										{code.BindPhone || "-"}
									</TableCell>
									{/* 绑定手机号时间 */}
									<TableCell className="whitespace-nowrap">
										{code.ActiveTime?DateUtils.formatDateTime(new Date(code.ActiveTime)):"-"}
									</TableCell>
									{/* 兑换状态 */}
									<TableCell className="whitespace-nowrap">
										{code.ExchangeStatus || "-"}
									</TableCell>
									{/* VIP状态 */}
									<TableCell className="whitespace-nowrap">
										{code.VipStatus ?VIP_STATUS_OPTIONS.filter(item => item.value == code.VipStatus )[0]?.key: "-"}
									</TableCell>
									{/* 核销状态 */}
									<TableCell className="whitespace-nowrap">
										{code.VerifyStatus==="unverified"?"未核销":"已核销"}
									</TableCell>
									{/* 核销时间 */}
									<TableCell className="whitespace-nowrap">
										{code.VerifyTime || "-"}
									</TableCell>
									{/* 激活时间 */}
									<TableCell className="whitespace-nowrap">
										{code.created_at?DateUtils.formatDateTime(new Date(code.created_at)):"-"}
									</TableCell>
									{/* 退款时间 */}
									<TableCell className="whitespace-nowrap">
										{code.RefundTime?DateUtils.formatDateTime(new Date(code.RefundTime)):"-"}
									</TableCell>
									{/* 备注 */}
									<TableCell className="whitespace-nowrap">
										{code.Remark || "-"}
									</TableCell>
									{/* 操作 */}
									 <TableCell>
										<div className="flex space-x-1">
											<TooltipProvider>
												<Tooltip>
													<TooltipTrigger asChild>
														<Button
															variant="ghost"
															size="icon"
															onClick={() => openSetInvalidDialog(code)}
														>
															{code.VerifyStatus==="unvalid" ? (
																<p className="h-4 w-4 text-red-500">已设置失效</p>
															) : (
																<p className="h-4 w-4 text-green-500">设置失效</p>
															)}
														</Button>
													</TooltipTrigger>
												</Tooltip>
											</TooltipProvider>
										</div>
									</TableCell>
								</TableRow>
							))
						)}
					</TableBody>
				</Table>
			</div>
			{!loading && codes.length > 0 && (
				<div className="flex items-center justify-between">
					<div className="text-sm text-gray-500 whitespace-nowrap">
						共 {total} 条兑换码记录，第 {page} 页 / 共 {totalPages} 页
					</div>
					<Pagination>
						<PaginationContent>
							<PaginationItem>
								<PaginationPrevious
									onClick={() => page > 1 && setPage(page - 1)}
									className={page <= 1 ? "pointer-events-none opacity-50" : ""}
								/>
							</PaginationItem>
							{Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
								let pageNum;
								if (totalPages <= 5) pageNum = i + 1;
								else if (page <= 3) pageNum = i + 1;
								else if (page >= totalPages - 2) pageNum = totalPages - 4 + i;
								else pageNum = page - 2 + i;
								return (
									<PaginationItem key={i}>
										<PaginationLink
											isActive={page === pageNum}
											onClick={() => setPage(pageNum)}
										>
											{pageNum}
										</PaginationLink>
									</PaginationItem>
								);
							})}
							<PaginationItem>
								<PaginationNext
									onClick={() => page < totalPages && setPage(page + 1)}
									className={
										page >= totalPages ? "pointer-events-none opacity-50" : ""
									}
								/>
							</PaginationItem>
						</PaginationContent>
					</Pagination>
				</div>
			)}
			{/* 设置失效、生效弹窗 */}
			<Dialog
        open={invalidateDialogOpen}
        onOpenChange={openSetInvalidDialogOpenChange}
        key="invalid"
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">{selectedCode?.VipStatus === "unvalid"?"设置生效":"设置失效"}</DialogTitle>
          </DialogHeader>
          <form
            ref={formRef}
            onSubmit={onSetInvalidFormSubmit}
            className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
          >
            <div className="space-y-4">
							<div className="grid grid-cols-6 items-center gap-4">
								<Label htmlFor="name" className="text-right">
									兑换码
								</Label>
								{/* 兑换码 */}
								{selectedCode?.Code}
							</div>
            </div>
						<div className="space-y-4">
							<div className="grid grid-cols-6 items-center gap-4">
								<Label htmlFor="name" className="text-right">
									状态
								</Label>
								{selectedCode?.VipStatus}
							</div>
            </div>
						<div className="space-y-4">
							<div className="grid grid-cols-6 items-center gap-4">
								<Label htmlFor="name" className="text-right">
									{selectedCode?.VipStatus === "unvalid"?"生效":"失效"}原因<span className="text-red-500">*</span>
								</Label>
								<Textarea
                  id="remark"
                  className="col-span-5"
									required
									value={invalidReason}
									placeholder={`请填写设置${selectedCode?.VipStatus === "unvalid"?"生效":"失效"}原因`}
                  onChange={(e) =>
										setInvalidReason(e.target.value)
                  }
                />
							</div>
            </div>
          </form>
          <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
              onClick={() => formRef.current?.requestSubmit()}
            >
              确认
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
			{/* 批量设置失效弹窗 */}
			<Dialog
        open={invalidBatchDialogOpen}
        onOpenChange={invalidBatchDialogOpenChange}
        key="invalid-batch"
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">批量设置失效</DialogTitle>
          </DialogHeader>
          <form
            ref={invalidBatchFormRef}
            onSubmit={onInvalidBatchFormSubmit}
            className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
          >
            <div className="space-y-4">
							<div className=" items-center gap-4">
								<Label htmlFor="file" className="text-right">
									上传需要设置失效的兑换码文件<span className="text-red-500">*</span>
								</Label>
								<Input type="file" name="file" required onChange={dealWithExcelData}/>
								<div className="width bg-gray-100 p-3 mt-3 text-sm">
									<p>文件模板:<a className="text-blue-500 cursor-pointer" onClick={() => downloadTemplate(codeService.getInvalidateTemplate,"invalidate")}>下载</a></p>
									<p className="lh-2">文件要求:仅支持xlsx格式的Excel文件</p>
								</div>
							</div>
            </div>
						<div className="space-y-4">
							<div className="items-center gap-4">
								<Label className="text-right">{`检测出${id}批次中的${excelData.length}个兑换码`}</Label>
							</div>
            </div>
						{
							!!excelData.length && (
							<div className="max-h-64 overflow-y-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="whitespace-nowrap text-center">兑换码</TableHead>
											<TableHead className="whitespace-nowrap text-center">操作状态</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{
											excelData.map(code => {
												if(typeof code !== "string"){
													const key = Object.keys(code)[0]
													return (<TableRow key={key}>
													<TableCell className="text-center">
														{key}
													</TableCell>
													<TableCell className="text-center">
														{
															batchSettingResList.length
															?batchSettingResList.includes(key)
																?"成功"
																:"失败"
															:"-"
														}
													</TableCell>
												</TableRow>)
												}
												return (<TableRow key={code}>
													<TableCell className="text-center">
														{code}
													</TableCell>
													<TableCell className="text-center">
														{
															batchSettingResList.length
															?batchSettingResList.includes(code)
																?"成功"
																:"失败"
															:"-"
														}
													</TableCell>
												</TableRow>)
											})
										}

									</TableBody>
								</Table>
							</div>
							)
						}
          </form>
          {!betchResponse && <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
							disabled={!excelData?.length}
              onClick={() => invalidBatchFormRef.current?.requestSubmit()}
            >
              确认
            </Button>
          </DialogFooter>}
        </DialogContent>
      </Dialog>
			{/* 批量核销弹窗 */}
			<Dialog
        open={checkBatchDialogOpen}
        onOpenChange={checkBatchDialogOpenChange}
        key="check-batch"
      >
        <DialogContent className="sm:max-w-xl bg-white">
          <DialogHeader>
            <DialogTitle className="text-center">批量核销</DialogTitle>
          </DialogHeader>
          <form
            ref={checkBatchFormRef}
            onSubmit={onCheckBatchFormSubmit}
            className="max-h-[70vh] overflow-y-auto p-1 pr-4 space-y-6"
          >
						<div className="space-y-4">
							<div className=" items-center gap-4">
								<Label htmlFor="file" className="text-right">
									上传需要核销的兑换码文件<span className="text-red-500">*</span>
								</Label>
								<Input type="file" name="file" required onChange={dealWithExcelData}/>
								<div className="width bg-gray-100 p-3 mt-3 text-sm">
									<p>文件模板:<a className="text-blue-500 cursor-pointer" onClick={() => downloadTemplate(codeService.getVerifyTemplate,"verify")}>下载</a></p>
									<p className="lh-2">文件要求:仅支持xlsx格式的Excel文件</p>
								</div>
							</div>
            </div>
						<div className="space-y-4">
							<div className="items-center gap-4">
								<Label className="text-right">{`检测出${id}批次中的${excelData.length}个兑换码`}</Label>
							</div>
            </div>
						{
							!!excelData.length && (
							<div className="max-h-64 overflow-y-scroll">
								<Table>
									<TableHeader>
										<TableRow>
											<TableHead className="whitespace-nowrap text-center ">兑换码</TableHead>
											<TableHead className="whitespace-nowrap text-center">操作状态</TableHead>
										</TableRow>
									</TableHeader>
									<TableBody>
										{
											excelData.map(code => {
												if(typeof code !== "string"){
													const key = Object.keys(code)[0]
													return (<TableRow key={key}>
													<TableCell className="text-center">
														{key}
													</TableCell>
													<TableCell className="text-center">
														{
															batchSettingResList.length
															?batchSettingResList.includes(key)
																?"成功"
																:"失败"
															:"-"
														}
													</TableCell>
												</TableRow>)
												}
												return (<TableRow key={code}>
													<TableCell className="text-center">
														{code}
													</TableCell>
													<TableCell className="text-center">
														{
															batchSettingResList.length
															?batchSettingResList.includes(code)
																?"成功"
																:"失败"
															:"-"
														}
													</TableCell>
												</TableRow>)
											})
										}

									</TableBody>
								</Table>
							</div>
							)
						}
          </form>
          {!betchResponse && <DialogFooter>
            <DialogClose asChild>
              <Button variant="outline">取消</Button>
            </DialogClose>
            <Button
							disabled={!excelData?.length}
              onClick={() => checkBatchFormRef.current?.requestSubmit()}
            >
              确认
            </Button>
          </DialogFooter>}
        </DialogContent>
      </Dialog>
			{/* 导出确认弹窗 */}
			<Dialog
				open={exportDialogOpen}
				onOpenChange={(open) => !open && setExportDialogOpen(false)}
				key="export"
			>
				<DialogContent className="sm:max-w-md bg-white">
					<DialogHeader>
						<DialogTitle>确认导出订单记录</DialogTitle>
						<DialogDescription>
							将当前筛选条件下的订单记录导出为Excel文件
						</DialogDescription>
					</DialogHeader>
					<div className="space-y-4 py-2">
						<div className="bg-gray-50 p-4 rounded-lg space-y-2">
							<div className="flex items-center justify-between">
								<span className="text-sm font-medium">导出记录数量:</span>
								<span className="font-medium text-blue-600">{total}</span>
							</div>
							{
								platformSearch && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">平台:</span>
										<span className="text-gray-600">{platformList.filter(({id}) => id == platformSearch)[0]?.label}</span>
									</div>
								)
							}
							{
								partnerSearch && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">合作伙伴:</span>
										<span className="text-gray-600">{partnerList?.filter(item => item.id === partnerSearch)[0]?.Name}</span>
									</div>
								)
							}
							{
								codeName && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">兑换码:</span>
										<span className="text-gray-600">{codeName}</span>
									</div>
								)
							}
							{time && (
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium">时间类型</span>
									<span className="text-gray-600">{
										TIME_OPTIONS.filter(item => item.key === time)[0].value
									}</span>
								</div>
							)}
							{time && timeStart && (
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium">{TIME_OPTIONS.filter(item => item.key === time)[0].value}开始时间:</span>
									<span className="text-gray-600">{timeStart}</span>
								</div>
							)}
							{time && timeEnd && (
								<div className="flex items-center justify-between">
									<span className="text-sm font-medium">{TIME_OPTIONS.filter(item => item.key === time)[0].value}结束时间:</span>
									<span className="text-gray-600">{timeEnd}</span>
								</div>
							)}
							{
								cardTypeSearch!=="0" && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">卡类型:</span>
										<span className="text-gray-600">{cardTypeList.filter(item => item.id == cardTypeSearch)[0]?.label}</span>
									</div>
								)
							}
							{
								cardDurationSearch!=="0" && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">会员时长:</span>
										<span className="text-gray-600">{[...officialCardList,...trialCardList].filter(item => item.id == cardDurationSearch)[0]?.label}</span>
									</div>
								)
							}
							{
								vipStatus !== "0" && (
									<div className="flex items-center justify-between">
										<span className="text-sm font-medium">VIP状态:</span>
										<span className="text-gray-600">{vipStatus=="active"?"已激活":"未激活"}</span>
									</div>
								)
							}
						</div>
						<p className="text-sm text-gray-500">
							导出的Excel文件将包含所有当前筛选条件下的兑换码记录数据
						</p>
					</div>
					<div className="flex justify-end gap-2 mt-4">
						<DialogClose asChild>
							<Button variant="outline">取消</Button>
						</DialogClose>
						<Button onClick={confirmExport} disabled={processingExport}>
							{processingExport ? (
								<>
									<div className="mr-2 h-4 w-4 animate-spin rounded-full border-2 border-t-transparent"></div>
									导出中...
								</>
							) : (
								<>
									<Download className="mr-2 h-4 w-4" />
									确认导出
								</>
							)}
						</Button>
					</div>
				</DialogContent>
			</Dialog>
		</div>
  )
}